import { useState } from "react";
import { Check, Copy, ExternalLink, MessagesSquare, Share2, Twitter, Crown } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { LogoAvatar, ScoreDonut } from "@/components/SignalBadges";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function ShareProfileModal({ open, onOpenChange, company }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/company/${company.slug}`;
  const socialLinks = [
    company.social_x
      ? { label: "X", handle: `@${company.social_x}`, href: `https://x.com/${company.social_x}`, icon: Twitter, testId: "share-card-x-link" }
      : null,
    company.social_reddit
      ? {
          label: "Reddit",
          handle: `u/${company.social_reddit}`,
          href: `https://reddit.com/user/${company.social_reddit}`,
          icon: MessagesSquare,
          testId: "share-card-reddit-link",
        }
      : null,
  ].filter(Boolean);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Profile link copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy the link automatically.");
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({
        title: `${company.name} on HighRank`,
        text: `${company.name} — HighRank Score ${company.highrank_score ?? "not yet scored"}`,
        url: shareUrl,
      });
    } catch {
      // User cancelled or native share is unavailable.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="profile-share-modal"
        className="bg-white border border-slate-200 sm:max-w-xl shadow-[0_24px_60px_rgba(0,0,0,0.10),0_4px_16px_rgba(0,0,0,0.06)]"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            <Share2 size={18} className="text-emerald-500" /> Share this listing
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-sm leading-relaxed">
            A branded preview with the company score, position and social links.
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="profile-share-card"
          className="share-card-dark relative overflow-hidden rounded-2xl border border-yellow-400/30 bg-[#050505] p-5 sm:p-6 shadow-[0_18px_45px_rgba(0,0,0,0.28),0_0_28px_rgba(234,179,8,0.12)]"
          style={{ backgroundColor: "#050505" }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_0%,rgba(234,179,8,0.12),transparent_36%),linear-gradient(135deg,rgba(234,179,8,0.06),transparent_45%)] pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4 mb-6">
            <Logo small />
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 font-mono2 text-[10px] uppercase tracking-[0.2em] text-yellow-400 border border-yellow-400/40 bg-yellow-400/10 rounded-full px-3 py-1">
                <Crown size={9} /> Pro listing
              </span>
            </div>
          </div>

          <div className="relative flex items-start gap-4">
            <LogoAvatar name={company.name} logoUrl={company.logo_url} size={56} testId="share-card-avatar" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono2 text-xs font-bold text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 rounded px-2 py-1">
                  #{company.rank_position}
                </span>
                <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">
                  {company.category} · {company.region}
                </span>
              </div>
              <h3 className="font-display text-4xl uppercase tracking-wide text-white mt-2 leading-none">{company.name}</h3>
              <p className="text-sm text-slate-400 mt-2 line-clamp-2">{company.tagline}</p>
            </div>
            <div className="text-center shrink-0">
              <ScoreDonut score={company.highrank_score} size={70} testId="share-card-score" />
              <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500 mt-2">HighRank Score</p>
            </div>
          </div>

          {socialLinks.length > 0 && (
            <div className="relative mt-5 pt-4 border-t border-white/10 flex items-center gap-2 flex-wrap" data-testid="share-card-social-links">
              {socialLinks.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={item.testId}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-slate-300 hover:text-emerald-300 hover:border-emerald-400/40 transition-colors"
                  >
                    <Icon size={13} /> {item.handle} <ExternalLink size={11} />
                  </a>
                );
              })}
            </div>
          )}

          <div className="relative mt-6 flex items-center justify-between gap-3">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 truncate">highrank.space/company/{company.slug}</span>
            <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-emerald-300">Discover on HighRank</span>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap mt-4">
          <button
            data-testid="share-copy-link-button"
            onClick={copyLink}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-sm rounded-full px-5 py-2.5 transition-colors"
          >
            {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy link"}
          </button>
          {"share" in navigator && (
            <button
              data-testid="share-native-button"
              onClick={nativeShare}
              className="inline-flex items-center gap-2 border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold text-sm rounded-full px-5 py-2.5 transition-colors"
            >
              <Share2 size={15} /> Share
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
